import { createSelector } from 'reselect'
import PRIVACY_STANDARDS from '../../../shared/constants/privacyStandards'
import { getFormValues, isValid } from 'redux-form'
import { formValueSelector } from 'redux-form'
import { FB_ONBOARDING, UACF_ONBOARDING, UACF_FB_ONBOARDING } from './constants'

export const selectAuth = (state) => state.auth

export const selectAuthToken = createSelector(
  selectAuth,
  (auth) => auth && auth.authToken
)

export const selectIsRefreshingToken = createSelector(
  selectAuth,
  (auth) => auth && auth.refreshingToken
)

export const selectFbAccessToken = createSelector(
  selectAuth,
  (auth) => auth && auth.fbAccessToken
)

export const selectFbUserId = createSelector(
  selectAuth,
  (auth) => auth && auth.fbUserId
)

export const selectFbStatus = createSelector(
  selectAuth,
  (auth) => auth && auth.fbStatus
)

export const selectFbExpiry = createSelector(
  selectAuth,
  (auth) => auth && auth.fbExpiresIn
)

export const selectFbData = createSelector(
  selectFbStatus,
  selectFbAccessToken,
  selectFbUserId,
  selectFbExpiry,
  (fbStatus, fbAccessToken, fbUserId, fbExpiresIn) => (
    {
      fbStatus,
      fbUserId,
      fbAccessToken,
      fbExpiresIn,
    }
  )
)

export const selectError = createSelector(
  selectAuth,
  (auth) => auth && auth.error
)

export const selectLoggingIn = createSelector(
  selectAuth,
  (auth) => auth && auth.loggingIn
)

export const selectOnboarding = createSelector(
  selectAuth,
  (auth) => auth && auth.onboarding
)

export const selectUacfId = createSelector(
  selectAuth,
  (auth) => auth && auth.uacfId
)

export const selectUacfProfile = createSelector(
  selectAuth,
  (auth) => auth && auth.uacfProfile
)

export const selectFbProfile = createSelector(
  selectAuth,
  (auth) => auth && auth.fbProfile
)

export const selectOnboardingEmail = createSelector(
  selectAuth,
  (auth) => auth && auth.onboardingEmail
)

export const selectGeoIp = createSelector(
  selectAuth,
  (auth) => auth && auth.geoip
)

export const selectRecaptchaResponse = createSelector(
  selectAuth,
  (auth) => auth && auth['g-recaptcha-response']
)

export const selectSignupValues = (formName) => (state) => getFormValues(formName)(state)
export const selectIsSignupValid = (formName) => (state) => isValid(formName)(state)

export const selectAuthCountry = createSelector(
  selectAuth,
  (auth) => auth && auth.country && auth.country.toLowerCase()
)

export const selectUacfInitialFormValues = createSelector(
  selectUacfProfile,
  (profile) => {
    if (!profile) { return null }

    let day = null
    let year = null
    let month = null
    let gdpr_iso_code = null// eslint-disable-line camelcase

    if (profile.birthdate) {
      const pieces = profile.birthdate.split('-')
      year = pieces[0]
      month = pieces[1]
      day = pieces[2]
    }

    const { location } = profile
    if (location && location.country) {
      gdpr_iso_code = location.country // eslint-disable-line camelcase
    }

    return {
      ...profile,
      day,
      month,
      year,
      birthdate: `${year}-${month}-${day}`,
      gdpr_iso_code, // eslint-disable-line camelcase
    }
  }
)

export const selectFbInitialFormValues = createSelector(
  selectFbProfile,
  (profile) => {
    if (!profile) { return null }
    let day = null
    let year = null
    let month = null
    let gender = null

    if (profile.gender === 'male') { gender = 'M' }
    if (profile.gender === 'female') { gender = 'F' }
    if (profile.birthday) {
      const pieces = profile.birthday.split('/')
      day = pieces[1]
      month = pieces[0]
      year = pieces[2]
    }

    return {
      day,
      month,
      year,
      gender,
      email: profile.email,
      firstName: profile.first_name,
      lastName: profile.last_name,
      birthdate: `${year}-${month}-${day}`,
    }
  }
)

export const selectOnboardingInitialFormValues = createSelector(
  selectOnboarding,
  selectUacfInitialFormValues,
  selectFbInitialFormValues,
  (onboarding, uacf, fb) => {
    if (onboarding === FB_ONBOARDING) { return fb }
    if (onboarding === UACF_ONBOARDING) { return uacf }
    if (onboarding === UACF_FB_ONBOARDING) {
      return { ...uacf, ...fb }
    }
  }
)

export const selectPrivacyStandard = createSelector(
  selectAuthCountry,
  (country) => country && PRIVACY_STANDARDS[country.toUpperCase()]
)

export const selectFbConnected = createSelector(
  selectFbStatus,
  (status) => status && status === 'connected'
)

export const selectSignupEmail = (formName) => (state) => {
  const valueSelector = formValueSelector(formName)
  return valueSelector(state, 'email')
}

export const selectStagedUser = createSelector(
  selectAuth,
  selectFbData,
  (auth, fbData) => {
    if (auth && auth.stagedUser) {
      return fbData ? { ...auth.stagedUser, ...fbData } : { ...auth.stagedUser }
    }
    return {}
  }
)

export const selectPromptOnboarding = createSelector(
  selectAuth,
  (auth) => { return auth.promptOnboarding }
)

export const selectIsWechatAuth = createSelector(
  selectAuth,
  (auth) => auth && auth.isWechatAuth
)
